import { supabase } from '../supabaseClient';

export type PrivacyReviewStatus = 'pending' | 'retained';

export interface PrivacyRetentionItem {
  id: string;
  category: string;
  record_count: number;
  default_legal_basis: string;
  default_retain_until: string | null;
  review_status: PrivacyReviewStatus;
  controller_legal_basis: string | null;
  retain_until: string | null;
}

export interface AccountDeletionReview {
  id: string;
  reference: string;
  requested_at: string;
  completed_at: string | null;
  review_due_at: string | null;
  review_status: PrivacyReviewStatus;
  reviewed_at: string | null;
  review_note: string | null;
  retained_summary: {
    categories?: string[];
    direct_identity_erased?: boolean;
    raw_location_erased?: boolean;
  };
  items: PrivacyRetentionItem[];
}

export interface RecordRetentionReviewParams {
  requestId: string;
  legalBasis: string;
  retainUntil: string;
  note?: string;
}

function normaliseReview(value: unknown): AccountDeletionReview {
  const row = value as Partial<AccountDeletionReview>;
  return {
    id: String(row.id ?? ''),
    reference: String(row.reference ?? ''),
    requested_at: String(row.requested_at ?? ''),
    completed_at: row.completed_at ?? null,
    review_due_at: row.review_due_at ?? null,
    review_status: row.review_status === 'retained' ? 'retained' : 'pending',
    reviewed_at: row.reviewed_at ?? null,
    review_note: row.review_note ?? null,
    retained_summary: row.retained_summary ?? {},
    items: Array.isArray(row.items) ? row.items : [],
  };
}

export const privacyRequestService = {
  async listAccountDeletionReviews(): Promise<AccountDeletionReview[]> {
    const { data, error } = await supabase.rpc('list_account_deletion_reviews');
    if (error) throw error;
    return (Array.isArray(data) ? data : []).map(normaliseReview);
  },

  async recordRetentionReview(params: RecordRetentionReviewParams): Promise<void> {
    const { error } = await supabase.rpc('review_account_deletion_retention', {
      p_request_id: params.requestId,
      p_legal_basis: params.legalBasis.trim(),
      p_retain_until: new Date(`${params.retainUntil}T23:59:59.999`).toISOString(),
      p_note: params.note?.trim() || null,
    });
    if (error) throw error;
  },
};
